import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
    Box,
    Paper,
    TextField,
    Button,
    Typography,
    Select,
    MenuItem,
    FormControl,
    Modal,
    CircularProgress,
    IconButton,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/system";
import { v4 as uuidv4 } from "uuid"; // UUID generator for apiid

// FIG K12 Logo
const FIGK12_LOGO_URL =
    "https://figk12pos.com/static/media/fig_CROP.cb0d7cf4314c63ba05ee.png";

const ChatContainer = styled(Box)({
    position: "relative",
    width: "350px",
    height: "1104px",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#1c1c1c",
    boxShadow: "-2px 0 5px rgba(0,0,0,0.5)",
    zIndex: 1000,
});

const ChatHeader = styled(Box)({
    padding: "16px",
    borderBottom: "1px solid #333",
    backgroundColor: "#2c2c2c",
    textAlign: "center",
    position: "relative",
});

const Logo = styled("img")({
    borderRadius: "50%",
    width: "80px",
    height: "80px",
    objectFit: "cover",
    marginBottom: "8px",
    border: "2px solid #ffffff",
});

const ChatMessages = styled(Box)({
    flex: 1,
    overflowY: "auto",
    padding: "16px",
    color: "#ffffff",
    fontWeight: "bold",
});

const ChatInput = styled(Box)({
    padding: "16px",
    borderTop: "1px solid #333",
    backgroundColor: "#2c2c2c",
});

const languageOptions = {
    English: ["English (US)", "English (UK)"],
    Español: [
        "Español (México)",
        "Español (España)",
        "Español (Colombia)",
        "Español (Argentina)",
        "Español (Caribe)",
    ],
    "中文": ["普通话 (Mandarin)", "廣東話 (Cantonese)"],
};

const SidebarChat = ({ onClose }) => {
    const [selectedLanguage, setSelectedLanguage] = useState("English");
    const [selectedDialect, setSelectedDialect] = useState(
        languageOptions["English"][0]
    );
    const [message, setMessage] = useState("");
    const [chatHistory, setChatHistory] = useState([]);
    const [isThinking, setIsThinking] = useState(false);
    const [expenseTotals, setExpenseTotals] = useState(null);
    const [incomeTotals, setIncomeTotals] = useState(null);
    const chatRef = useRef(null);

    // Load the two view endpoints into context on mount
    useEffect(() => {
        const loadContextData = async () => {
            try {
                const [expRes, incRes] = await Promise.all([
                    axios.get(
                        "https://figazuresql.swancreate.com/api/swa/vw_aichat_ExpenseTotalsMonthly"
                    ),
                    axios.get(
                        "https://figazuresql.swancreate.com/api/swa/vw_aichat_IncomeTotalsMonthly"
                    ),
                    axios.get(
                        "https://figazuresql.swancreate.com/api/swa/FINANCE_Categories"
                    ),
                    axios.get(
                        "https://figazuresql.swancreate.com/api/swa/FINANCE_SubCategories"
                    ),




                ]);
                setExpenseTotals(expRes.data);
                setIncomeTotals(incRes.data);
            } catch (err) {
                console.error("Error loading context data:", err);
            }
        };
        loadContextData();
    }, []);

    // Now accepts a full prompt (including history and current turn)
    const getChatbotResponse = async (fullPrompt) => {
        try {
            const response = await axios.post(
                "https://aiendpoint.swancreate.com/api/ai/gpt",
                { prompt: fullPrompt },
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );
            return response.data.response || "No response.";
        } catch (error) {
            console.error("Error fetching response:", error);
            return "Sorry, there was an error processing your request.";
        }
    };

    // Log chat history to the logging endpoint
    const logChat = async (promptText, responseText) => {
        try {
            const logData = {
                UserID: "00000000-0000-0000-0000-000000000002", // Hardcoded user GUID
                OrganizationID: "77778645-FB4E-4190-AB2E-DCCE87BDF7E0",
                BotID: "Milwaukee Public Schools",
                PromptText: promptText,
                ResponseText: responseText,
            };

            const apiEndpoint =
                "https://figazuresql.swancreate.com/api/swa/AI_PromptResponseLog";
            await axios.post(apiEndpoint, logData);
        } catch (error) {
            console.error(
                "Error logging chat:",
                error.response?.data || error.message
            );
        }
    };

    // Auto-scroll on new messages
    useEffect(() => {
        if (chatRef.current) {
            chatRef.current.scrollTop = chatRef.current.scrollHeight;
        }
    }, [chatHistory]);

    const handleLanguageChange = (event) => {
        const language = event.target.value;
        setSelectedLanguage(language);
        setSelectedDialect(languageOptions[language][0]);
    };

    const handleDialectChange = (event) => {
        setSelectedDialect(event.target.value);
    };

    const handleSendMessage = async () => {
        if (message.trim() === "") return;

        const userMessage = { sender: "user", message };
        setChatHistory((prev) => [...prev, userMessage]);
        setIsThinking(true);

        // Build full transcript from history
        const transcript = [
            `System: You are an assistant proficient in ${selectedLanguage} (${selectedDialect}). Provide USDA-compliant responses.  You have the current year actual sales and incomes and Budgets totals in context.  You are a K12 nutrition department operations expert.  Ensure that when talking about finances, you recomendations follow USDA best practives for school districts`
        ]
            .concat(
                chatHistory.map((c) =>
                    c.sender === "user"
                        ? `User: ${c.message}`
                        : `Assistant: ${c.message}`
                )
            )
            .join("\n");

        // Append current turn
        const fullPrompt = `${transcript}\nUser: ${message}\nAssistant:`;

        // Call with full context
        const botResponse = await getChatbotResponse(fullPrompt);
        const botMessage = { sender: "bot", message: botResponse };
        setChatHistory((prev) => [...prev, botMessage]);

        await logChat(transcript + `\nUser: ${message}`, botResponse);

        setMessage("");
        setIsThinking(false);
    };

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        alert("Response copied to clipboard!");
    };

    return (
        <ChatContainer>
            <ChatHeader>
                <IconButton
                    sx={{ position: "absolute", top: "8px", right: "8px", color: "#ffffff" }}
                    onClick={onClose}
                >
                    <CloseIcon />
                </IconButton>
                <Logo src={FIGK12_LOGO_URL} alt="FIG K12 Logo" />
                <Typography variant="h6" style={{ fontWeight: "bold", color: "#ffffff" }}>
                    Chat Assistant
                </Typography>
                <Typography variant="body2" style={{ color: "#cccccc" }}>
                    USDA, NSLP, and meal planning assistant.
                </Typography>
            </ChatHeader>

            <Box p={2} display="flex" justifyContent="space-between">
                <FormControl sx={{ minWidth: "48%" }}>
                    <Select
                        value={selectedLanguage}
                        onChange={handleLanguageChange}
                        variant="outlined"
                        sx={{
                            color: "#ffffff",
                            fontWeight: "bold",
                            "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#ffffff",
                            },
                        }}
                    >
                        {Object.keys(languageOptions).map((language) => (
                            <MenuItem key={language} value={language}>
                                {language}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl sx={{ minWidth: "48%" }}>
                    <Select
                        value={selectedDialect}
                        onChange={handleDialectChange}
                        variant="outlined"
                        sx={{
                            color: "#ffffff",
                            fontWeight: "bold",
                            "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#ffffff",
                            },
                        }}
                    >
                        {languageOptions[selectedLanguage].map((dialect) => (
                            <MenuItem key={dialect} value={dialect}>
                                {dialect}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <ChatMessages ref={chatRef}>
                {chatHistory.map((chat, index) => (
                    <Box
                        key={index}
                        mb={2}
                        display="flex"
                        justifyContent={chat.sender === "user" ? "flex-end" : "flex-start"}
                    >
                        <Paper
                            elevation={1}
                            sx={{
                                padding: "8px 12px",
                                backgroundColor: chat.sender === "user" ? "#007bff" : "#333333",
                                color: "#ffffff",
                                fontWeight: "bold",
                                maxWidth: "80%",
                                position: "relative",
                            }}
                        >
                            {chat.message}
                            {chat.sender === "bot" && (
                                <IconButton
                                    onClick={() => handleCopy(chat.message)}
                                    size="small"
                                    sx={{
                                        position: "absolute",
                                        top: 4,
                                        right: 4,
                                        color: "#ffffff",
                                    }}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            )}
                        </Paper>
                    </Box>
                ))}
            </ChatMessages>

            <Box p={2}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Type your message..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "#ffffff" },
                            color: "#ffffff",
                            fontWeight: "bold",
                        },
                    }}
                    onKeyPress={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            handleSendMessage();
                        }
                    }}
                />
                <Button variant="contained" color="primary" onClick={handleSendMessage} sx={{ mt: 1, width: "100%" }}>
                    Send
                </Button>
                <Button sx={{ mt: 1, width: "100%" }} variant="text" color="secondary" onClick={onClose}>
                    Close
                </Button>
            </Box>

            <Modal
                open={isThinking}
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "rgba(0,0,0,0.5)",
                }}
            >
                <CircularProgress color="primary" />
            </Modal>
        </ChatContainer>
    );
};

export default SidebarChat;
